import React, { useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import SVButton from 'components/elements/SVButton';

interface ArchiveModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
  verificationName: string;
  mode?: 'archive' | 'unarchive';
  isSubmitting?: boolean;
}

const ArchiveModal: React.FC<ArchiveModalProps> = ({
  show,
  onHide,
  onConfirm,
  verificationName,
  mode = 'archive',
  isSubmitting = false,
}) => {
  const [confirmValue, setConfirmValue] = useState('');
  const [reason, setReason] = useState('');
  const confirmText = 'archive';
  const isUnarchive = mode === 'unarchive';
  const title = isUnarchive ? 'Unarchive Verification' : 'Archive Verification';
  const confirmButtonLabel = isUnarchive ? 'Unarchive' : 'Archive';
  const canConfirm = isUnarchive
    ? reason.trim().length > 0
    : confirmValue.toLowerCase() === confirmText;

  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm(reason.trim());
      setConfirmValue('');
      setReason('');
    }
  };

  const handleClose = () => {
    setConfirmValue('');
    setReason('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isUnarchive ? (
          <>
            <p>
              Are you sure you want to unarchive the verification for
              {' '}
              <strong>{verificationName}</strong>
              ?
            </p>
            <Form.Group controlId="unarchiveReason">
              <Form.Label>
                Please enter a reason for unarchiving.
              </Form.Label>
              <Form.Control
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason"
                disabled={isSubmitting}
              />
            </Form.Group>
          </>
        ) : (
          <>
            <p>
              Are you sure you want to archive the verification for
              {' '}
              <strong>{verificationName}</strong>
              ?
            </p>
            <p>Archived verifications can be restored later.</p>
            <Form.Group controlId="archiveReason" className="mb-3">
              <Form.Label>Reason (optional)</Form.Label>
              <Form.Control
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason"
                disabled={isSubmitting}
              />
            </Form.Group>
            <Form.Group controlId="archiveConfirm">
              <Form.Label>
                To confirm, please type
                {' '}
                &quot;
                <strong>{confirmText}</strong>
                &quot;
                {' '}
                in the field below.
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={`Type "${confirmText}" here`}
                value={confirmValue}
                onChange={(e) => setConfirmValue(e.target.value)}
                disabled={isSubmitting}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <SVButton variant="cancel" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </SVButton>
        <SVButton
          variant="warning"
          onClick={handleConfirm}
          disabled={!canConfirm || isSubmitting}
        >
          {confirmButtonLabel}
        </SVButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ArchiveModal;
